/* global React */
import { Box, Grid } from '@mui/material'
import { format, parseISO } from 'date-fns'
import Text from 'ui/Element/Text'
import { type MessageVariant } from 'ui/Element/Message/List/Template/Message'

export interface FooterBaseProps {
  /** a person's name */
  authorName?: string
  /** an ISO date string */
  timestamp?: string
  /** timestamp when last edited */
  editTimestamp?: string | null
  /** text to be displayed while a message is being sent */
  sendingLabel?: string
  /** translation for 'Edited' */
  editedLabel?: string
  /** whether to return null instead of the element */
  hidden?: boolean
  /** message variant (drives alignment) */
  variant?: MessageVariant
}

interface FooterProps extends FooterBaseProps {
  /** a css class to set for the root element */
  className?: string
  /** optional me label */
  meLabel?: string
}

/**
 * Footer Component
 *
 * Displays the name of the author and the creation date of the message.
 *
 * @param props               the component's properties
 * @param props.authorName    a person's name
 * @param props.timestamp     an ISO date string
 * @param props.editTimestamp timestamp when last edited
 * @param props.className     a css class to set for the root element
 * @param props.sendingLabel  text to be displayed while a message is being sent
 * @param props.meLabel       optional me label
 * @param props.editedLabel   translation for 'Edited'
 * @param props.hidden        whether to return null instead of the element
 * @param props.variant       the variant of the message
 * @returns                   the message footer, or null
 */
const Footer = ({
  authorName,
  timestamp,
  editTimestamp,
  className,
  sendingLabel,
  meLabel,
  editedLabel,
  hidden,
  variant,
}: FooterProps) => (hidden ? null : (
  <Grid component={Box} className={className}>
    <Text
      component={'span'}
      variant={'12/regular'}
      color={'gray.600'}
    >
      {authorName || (variant === 'primary' && timestamp && meLabel)}
    </Text>
    <Text
      component={'span'}
      variant={'12/regular'}
      color={'gray.600'}
    >
      &nbsp;{timestamp ? `- ${format(parseISO(timestamp), 'HH:mm')}` : `${sendingLabel}`}
    </Text>
    {editTimestamp && (
      <Text
        component={'span'}
        variant={'12/regular'}
        color={'gray.700'}
        style={{
          fontStyle: 'italic',
          paddingRight: '0.125rem',
        }}
      >
        {`, ${editedLabel} ${format(parseISO(editTimestamp), 'HH:mm')}`}
      </Text>
    )}
  </Grid>
))

export default Footer
